import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GetTenantHistory, TenantMailProcessingRow } from "./CoreAPI";
import { appendPoint, seriesFromHistory, trimToWindow, TrendSeries } from "./trendSeries";

/** The window every fan-out request asks for. The server permits up to 2 days. */
export const TREND_WINDOW_HOURS = 24;

/** Requests in flight at once. 40 at once would hammer the API for no gain. */
export const FANOUT_CONCURRENCY = 6;

export type TrendsState = "idle" | "loading" | "ready" | "failed";

export interface iUseTenantTrends {
    state: TrendsState;
    /** tenantKey to series. A key is absent until its request succeeds. */
    series: Map<string, TrendSeries>;
    /** Tenants whose history could not be read. Their cells show "no data". */
    failedKeys: Set<string>;
    /** Re-runs the fan-out from scratch. For recovering from `failed`, or after a per-tenant failure. */
    reload: () => void;
}

/**
 * Runs `worker` over `items` with at most `limit` in flight.
 *
 * Each of the `min(limit, items.length)` runners loops on a SHARED cursor
 * (`next`): it claims an index synchronously, before its first `await`, so
 * no two runners can ever claim the same index and no index goes unclaimed
 * while any runner is still looping — items are neither skipped nor
 * processed twice, regardless of how many runners there are or how long any
 * one `worker` call takes.
 *
 * `worker` MUST NOT reject. `Promise.allSettled` is used here rather than
 * `Promise.all` so that if it does anyway, only the runner that hit the
 * rejection stops claiming further items — its siblings keep draining the
 * shared cursor, and `pool` still resolves only once every runner has
 * settled, instead of resolving early while a rejected-away runner's
 * siblings are still running "detached" from whatever is awaiting `pool`.
 * This function does not, and should not, swallow that rejection in a
 * try/catch: a worker that throws is a bug in the caller, and hiding it
 * here would make `pool` itself untrustworthy for its own next caller. A
 * caller that needs a guaranteed terminal outcome despite a broken worker
 * arranges that itself — see the `.catch()` below `pool`'s call site in
 * this file.
 */
async function pool<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
    let next = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (next < items.length) {
            const index = next;
            next += 1;
            await worker(items[index]);
        }
    });
    await Promise.allSettled(runners);
}

/**
 * Fetches every tenant's history once, then keeps it current from the overview.
 *
 * Two effects with deliberately different triggers:
 *
 * The fan-out runs when the ROSTER changes, keyed on an order-INSENSITIVE
 * signature of tenant keys. That matters because the overview is
 * "server-ordered worst-first" per contract, with no documented tie-break —
 * the same 40 tenants can legitimately reorder between polls as one crosses
 * a health boundary, and keying on order would refetch all 40 on an
 * ordinary poll that changed nothing about the roster itself.
 *
 * The append runs when a NEW OVERVIEW lands, keyed on `asOf`. It is emphatically
 * NOT keyed on `rows`: the page shell derives that array as
 * `overview?.tenants ?? []`, a fresh identity on every render, so keying on it
 * would append a point per render. `asOf` changes only when the server actually
 * reports a new snapshot. Phase 1's review caught the identical trap in a
 * `useMemo` dependency array.
 *
 * The two effects interleave safely: both write `series` through a
 * functional updater, so neither can clobber a write the other made first,
 * and the append effect simply skips a tenant not yet present in the map
 * rather than racing the fan-out to create it.
 *
 * Per-tenant failures are isolated: one tenant's 500 degrades that tenant's
 * cell and nothing else. `failed` is reserved for every tenant failing, which
 * is a different situation and gets a different message. `reload` re-runs
 * the whole fan-out on demand, for recovering from either.
 */
export function useTenantTrends(
    sysAdminToken: string | undefined,
    rows: TenantMailProcessingRow[],
    asOf: string | undefined,
): iUseTenantTrends {
    const [state, setState] = useState<TrendsState>(sysAdminToken ? "loading" : "idle");
    const [series, setSeries] = useState<Map<string, TrendSeries>>(new Map());
    const [failedKeys, setFailedKeys] = useState<Set<string>>(new Set());
    const requestId = useRef(0);

    const keys = rows.map(row => row.tenantKey).filter((key): key is string => key !== undefined);

    // Order-insensitive: see the function doc comment above for why. Used
    // only as a change signal below, never split back into a key list — a
    // tenantKey is arbitrary text (CoreAPI.ts already has to
    // encodeURIComponent it for the URL), so joining and re-splitting one
    // would fetch the wrong tenants if a key ever contained the join
    // character.
    const rosterSignature = keys.slice().sort().join(" ");

    // Stable identity for the fetch list, refreshed only when the signature
    // above changes — so a pure worst-first reorder within the same set of
    // tenants does not produce a new array reference and does not refetch.
    // `keys` itself is a fresh array every render and cannot be depended on
    // directly for the same reason `rows` cannot be (see the doc comment on
    // the append effect below). Deliberately returns `keys` — the server's
    // actual worst-first order — rather than the sorted signature, so the
    // fetch itself still asks for the worst tenants first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rosterKeys = useMemo(() => keys, [rosterSignature]);

    const runFanout = useCallback((): void => {
        if (!sysAdminToken || rosterKeys.length === 0) {
            return;
        }

        requestId.current += 1;
        const mine = requestId.current;

        // Spec §9 requires the client to constrain the window rather than let
        // the server reject it. That is satisfied structurally here: the window
        // is computed as a fixed 24 hours back from now, so it cannot exceed
        // the server's 2-day limit. If TREND_WINDOW_HOURS is ever raised, it
        // must stay at or under 48.
        const to = new Date();
        const from = new Date(to.getTime() - TREND_WINDOW_HOURS * 3_600_000);

        setState("loading");
        // Load-bearing, not tidy: prunes a tenant that has left the roster.
        // Window enforcement rides on `trimToWindow` in the append effect,
        // which only reaches tenants present in the current poll's rows, so
        // a departed tenant whose series survived would never be trimmed
        // again while `aggregateSeries` kept summing its aged-out points.
        setSeries(new Map());
        setFailedKeys(new Set());

        // Mirrors exactly what gets added to `failedKeys` below, so "did
        // every tenant fail" is read from the same set the UI renders from
        // rather than a second, independently-maintained total that could
        // in principle drift from it.
        const failedThisRun = new Set<string>();

        void pool(rosterKeys, FANOUT_CONCURRENCY, async key => {
            // Checked before AND after the await. Before: a runner that
            // already knows this run is stale stops claiming further items
            // instead of draining the whole remaining queue against a
            // roster nobody wants anymore. After: a request already in
            // flight when this run went stale still needs to be discarded
            // on return, since nothing stopped it from being issued.
            if (requestId.current !== mine) {
                return;
            }
            const response = await GetTenantHistory(sysAdminToken, key, from, to);
            if (requestId.current !== mine) {
                return;
            }

            if (response.isOk && response.data) {
                const built = seriesFromHistory(key, response.data.rows);
                setSeries(held => new Map(held).set(key, built));
                return;
            }

            if (response.status === 404) {
                // The overview returned this tenant and the history store does
                // not have it. Not fatal, but the two disagree about the
                // roster, which someone should know.
                console.warn(`No mail-processing history for tenant "${key}" that the overview returned.`);
            }
            failedThisRun.add(key);
            setFailedKeys(held => new Set(held).add(key));
        })
            .then(() => {
                if (requestId.current !== mine) {
                    return;
                }
                setState(failedThisRun.size === rosterKeys.length ? "failed" : "ready");
            })
            .catch(() => {
                // Defensive only: `pool`'s worker above must not reject (see
                // its doc comment), and `pool` uses Promise.allSettled so a
                // rejection would not even propagate here today. This exists
                // so the state machine cannot wedge on "loading" forever
                // regardless of what a future change to either makes true.
                if (requestId.current === mine) {
                    setState("failed");
                }
            });
    }, [sysAdminToken, rosterKeys]);

    useEffect(() => {
        if (!sysAdminToken) {
            // Mirrors useTenantOverview: deselecting the tenant drops stale
            // data rather than leaving the previous token's series on screen.
            setState("idle");
            setSeries(new Map());
            setFailedKeys(new Set());
            return;
        }

        if (rosterKeys.length === 0) {
            // A token with nothing to fetch is a TERMINAL state, not a wait.
            // Leaving this "idle" rendered as a permanent "Loading the last
            // 24 hours…" for a zero-tenant roster, since Task 10 collapses
            // "idle" and "loading" into one loading message — contradicting
            // the table's own empty state sitting below it.
            setState("ready");
            setSeries(new Map());
            setFailedKeys(new Set());
            return;
        }

        runFanout();
        return () => {
            // Makes an unmount, or simply moving on to a new run, act as
            // cancellation: any response for this run that arrives after
            // this point fails the requestId check above and writes nothing.
            requestId.current += 1;
        };
    }, [sysAdminToken, rosterKeys, runFanout]);

    useEffect(() => {
        if (asOf === undefined) {
            return;
        }

        // Read the clock ONCE, out here. A state updater must be a pure
        // function of its arguments: React invokes updaters during render-phase
        // queue processing and double-invokes them under StrictMode, so reading
        // Date.now() inside would make the updater impure — and reading it
        // per-tenant would trim the forty series to forty microsecond-different
        // windows, making "every series covers the same window" only
        // approximately true when it can be exactly true for free.
        const cutoff = Date.now() - TREND_WINDOW_HOURS * 3_600_000;

        setSeries(held => {
            let changed = false;
            const next = new Map(held);
            for (const row of rows) {
                const key = row.tenantKey;
                if (key === undefined) {
                    continue;
                }
                const existing = next.get(key);
                if (!existing) {
                    continue;
                }
                const trimmed = trimToWindow(appendPoint(existing, row), cutoff);
                /*
                    Compare against `existing`, NOT against `appendPoint`'s
                    result.

                    Both functions return the object they were handed when
                    they change nothing — but that does NOT make the two
                    comparisons equivalent. Only one of them is correct.

                    Append succeeds, trim drops nothing (the common case):
                    appendPoint returns a NEW object, and trimToWindow
                    returns THAT SAME object unchanged. `trimmed !==
                    appended` is therefore false, and a version of this
                    check written that way would silently discard every
                    ordinary append — the one thing this effect exists to
                    do. `trimmed !== existing` is true here, and catches it.

                    Append rejects, trim drops (a tenant that has stopped
                    reporting but still has an aged-out point to shed):
                    appendPoint returns `existing` unchanged, so `trimmed
                    !== appended` and `trimmed !== existing` are the SAME
                    expression here — either form works for this case
                    alone. It is the case above, not this one, that makes
                    `existing` the only correct choice.
                */
                if (trimmed !== existing) {
                    next.set(key, trimmed);
                    changed = true;
                }
            }
            return changed ? next : held;
        });
        // `rows` is intentionally absent from the dependency list — see the
        // doc comment. `asOf` is the signal that a new overview landed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asOf]);

    return { state, series, failedKeys, reload: runFanout };
}
